import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import annArborPour from "@/assets/deep-self-leveling-underlayment-pour-ann-arbor-mi.webp";
import needleRoller from "@/assets/needle-rolling-self-leveling-underlayment-1-wood-floors-northville.webp";
import sluPour from "@/assets/engineered-self-leveling-underlayment-concrete-livonia.webp";

const fade = { initial: { opacity: 0, y: 28 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

const projects = [
  {
    slug: "/projects/ann-arbor-subfloor",
    location: "Ann Arbor, MI",
    headline: "1.5-Inch Structural Correction & Silica Dust Control",
    teaser: "How our 8-man crew safely executed a massive 1.5-inch correction using HEPA-filtered mixing stations.",
    image: annArborPour,
    alt: "Deep self leveling underlayment pour correcting a 1.5 inch height variance, Ann Arbor, MI",
  },
  {
    slug: "/projects/northville-walnut",
    location: "Northville, MI",
    headline: "Precision Flatness for 8-Inch Walnut",
    teaser: "Engineering a flawless, glass-like subfloor to meet strict NWFA tolerances for custom wide-plank walnut.",
    image: needleRoller,
    alt: "Needle roller releasing air from self leveling underlayment, Northville, MI",
  },
  {
    slug: "/projects/livonia-subfloor",
    location: "Livonia, MI",
    headline: "Smart Encapsulation & Custom Hickory",
    teaser: "Bypassing hazardous mastic grinding with specialized bonding primers and engineered underlayment.",
    image: sluPour,
    alt: "Self leveling underlayment poured over primed concrete, Livonia, MI",
  },
];

const ProjectSpotlights = () => (
  <section className="bg-[hsl(var(--neutral-950))] text-primary-foreground py-16 md:py-24 lg:py-32">
    <div className="max-w-[1200px] mx-auto px-5 md:px-12">
      <motion.div {...fade} className="mb-12 md:mb-16">
        <p className="text-[10px] md:text-[11px] font-semibold tracking-[.18em] uppercase text-[hsl(var(--gold-400))] mb-2">
          Case Studies
        </p>
        <h2 className="font-display text-[clamp(28px,5vw,48px)] font-semibold leading-[1.1]">
          Project Spotlights: <em className="italic font-normal text-[hsl(var(--gold-300))]">Subfloor Engineering in Action.</em>
        </h2>
      </motion.div>

      <div className="grid md:grid-cols-3 gap-5 md:gap-6">
        {projects.map((p, i) => (
          <motion.a
            key={i}
            href={p.slug}
            {...fade}
            transition={{ delay: i * 0.08 }}
            className="group flex flex-col bg-[hsl(var(--neutral-900))] rounded-2xl overflow-hidden border border-[hsl(var(--neutral-800))] hover:border-[hsl(var(--gold-400))]/30 transition-colors duration-500"
          >
            {/* Image */}
            <div className="relative aspect-[16/10] overflow-hidden">
              <img width={800} height={534} loading="lazy"
                src={p.image}
                alt={p.alt}
                className="absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-out grayscale-[20%] contrast-[1.1] group-hover:grayscale-0 group-hover:scale-[1.04]"
              />
            </div>

            {/* Content */}
            <div className="flex flex-col flex-1 p-5 md:p-6">
              <span className="inline-block text-[10px] font-bold tracking-[.16em] uppercase text-[hsl(var(--gold-400))] bg-[hsl(var(--gold-400))]/10 border border-[hsl(var(--gold-400))]/20 rounded-full px-3 py-1 w-fit mb-3">
                {p.location}
              </span>
              <h3 className="font-display text-lg md:text-xl font-semibold leading-snug mb-3">
                {p.headline}
              </h3>
              <p className="text-[12px] md:text-[13px] font-light text-primary-foreground/50 leading-relaxed mb-5 flex-1">
                {p.teaser}
              </p>
              <span className="inline-flex items-center gap-1.5 text-[11px] md:text-[12px] font-semibold tracking-wide text-[hsl(var(--gold-400))] group-hover:text-[hsl(var(--gold-300))] transition-colors">
                View Project Details
                <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </div>
          </motion.a>
        ))}
      </div>
    </div>
  </section>
);

export default ProjectSpotlights;
